import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { EventCardSkeleton } from '@/components/ui/skeleton';
import { NoEventsState, NoSearchResultsState, NetworkErrorState } from '@/components/EmptyState';
import { CustomerBottomNav } from '@/components/BottomNav';
import { 
  Ticket, 
  Calendar, 
  Clock, 
  MapPin, 
  User,
  LogOut,
  Menu,
  X,
  Search,
  RefreshCw,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { GAM_TICKET_LOGO } from '@/config/assets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const { isAuthenticated, isAdmin, isCustomer, user, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchEvents = useCallback(async (search = '', isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await axios.get(`${API_URL}/api/events/public?${params.toString()}`);
      setEvents(response.data);
      
      if (isRefresh) toast.success('Events refreshed');
    } catch (err) {
      console.error('Failed to fetch events:', err);
      setError(err.message || 'Failed to load events');
      if (isRefresh) toast.error('Failed to refresh events');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      fetchEvents(value);
    }, 300));
  };

  const handleRefresh = () => {
    fetchEvents(searchQuery, true);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getEventTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'football': return 'bg-green-100 text-green-700';
      case 'concert': return 'bg-purple-100 text-purple-700';
      case 'festival': return 'bg-orange-100 text-orange-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header - Mobile Optimized */}
      <header className="bg-white border-b border-border sticky top-0 z-50 safe-top">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo - Circular */}
            <Link to="/events" className="flex items-center gap-2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-[#0055FF]/20 shadow-sm">
                <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/events" className="text-foreground font-medium hover:text-primary transition-colors">
                Events
              </Link>
              {isAuthenticated && isCustomer && (
                <Link to="/my-tickets" className="text-muted-foreground font-medium hover:text-foreground transition-colors">
                  My Tickets
                </Link>
              )}
              {isAuthenticated && isAdmin && (
                <Link to="/dashboard" className="text-muted-foreground font-medium hover:text-foreground transition-colors">
                  Admin Dashboard
                </Link>
              )}
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">{user?.name || user?.email}</span>
                  <Button variant="ghost" size="sm" onClick={handleLogout} className="btn-press">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="btn-press">Sign In</Button>
                  </Link>
                  <Link to="/user-register">
                    <Button size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] btn-press">Register</Button>
                  </Link>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 touch-target"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-border mt-3 pt-3 pb-2 animate-fade-in">
              <nav className="flex flex-col gap-1">
                <Link 
                  to="/events" 
                  className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-muted transition-colors touch-target"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="font-medium">Events</span>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </Link>
                {isAuthenticated && isCustomer && (
                  <Link 
                    to="/my-tickets" 
                    className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-muted transition-colors touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="font-medium">My Tickets</span>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                )}
                {isAuthenticated && isAdmin && (
                  <Link 
                    to="/dashboard"
                    className="flex items-center justify-between py-3 px-2 rounded-lg hover:bg-muted transition-colors touch-target"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="font-medium">Admin Dashboard</span>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                )}
                <div className="border-t border-border mt-2 pt-2">
                  {isAuthenticated ? (
                    <Button 
                      variant="ghost" 
                      onClick={handleLogout} 
                      className="w-full justify-start py-3 h-auto touch-target"
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      Logout
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="outline" className="w-full h-12 btn-press">Sign In</Button>
                      </Link>
                      <Link to="/user-register" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full h-12 bg-[#0055FF] hover:bg-[#0044CC] btn-press">Register</Button>
                      </Link>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0055FF] to-[#0044CC] text-white py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-2">
            Football Matches & Events
          </h1>
          <p className="text-center text-white/80 text-sm sm:text-base mb-6 max-w-md mx-auto">
            Get your tickets for the best football matches and events in The Gambia
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search events by name, venue..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-12 pr-12 h-12 sm:h-14 bg-white text-foreground rounded-full border-0 shadow-lg text-base"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); fetchEvents(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-full touch-target"
                  aria-label="Clear search"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl sm:text-2xl font-bold">
            {searchQuery ? 'Search Results' : 'Upcoming Events'}
          </h2>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-press"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <EventCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <NetworkErrorState onRetry={() => fetchEvents(searchQuery)} />
        )}

        {/* Empty State */}
        {!loading && !error && events.length === 0 && (
          searchQuery ? (
            <NoSearchResultsState 
              query={searchQuery} 
              onClear={() => { setSearchQuery(''); fetchEvents(); }} 
            />
          ) : (
            <NoEventsState onRefresh={handleRefresh} />
          )
        )}

        {/* Events Grid */}
        {!loading && !error && events.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {events.map((event, index) => (
              <Card 
                key={event.id} 
                className="overflow-hidden bg-white border-border hover:shadow-lg transition-all duration-200 card-interactive animate-fade-in-up"
                style={{ animationDelay: `${index * 0.03}s` }}
                data-testid={`event-card-${event.id}`}
              >
                {/* Event Image - 16:9 aspect ratio with rounded corners */}
                <div className="relative aspect-video bg-[#0055FF] overflow-hidden rounded-t-lg">
                  {event.flyer_url ? (
                    <img 
                      src={event.flyer_url} 
                      alt={event.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Ticket className="w-10 h-10 text-white/30" />
                    </div>
                  )}
                  {/* Event Type Badge */}
                  <Badge className={`absolute top-2 left-2 text-xs ${getEventTypeColor(event.event_type)}`}>
                    {event.event_type || 'Event'}
                  </Badge>
                </div>

                <CardContent className="p-3">
                  {/* Event Name */}
                  <h3 className="font-heading font-bold text-sm sm:text-base mb-2 line-clamp-1">
                    {event.name}
                  </h3>

                  {/* Event Details - Compact */}
                  <div className="space-y-1 text-xs sm:text-sm">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{event.venue}</span>
                    </div>
                  </div>

                  {/* Price & CTA - Compact */}
                  <div className="flex items-center justify-between pt-3 mt-3 border-t border-border">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">From</p>
                      <p className="font-heading text-base sm:text-lg font-bold text-[#0055FF]">
                        D{event.ticket_price}
                      </p>
                    </div>
                    <Link to={`/event/${event.id}`}>
                      <Button 
                        size="sm" 
                        className="bg-[#0055FF] hover:bg-[#0044CC] btn-press h-7 sm:h-8 px-2 sm:px-3 text-xs"
                        data-testid={`view-event-${event.id}`}
                      >
                        View
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-border py-6 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#0055FF]/20 shadow-sm">
              <img src={GAM_TICKET_LOGO} alt="Gam Ticket" className="w-full h-full object-cover" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            The premier ticketing platform for football matches and events in The Gambia
          </p>
          <div className="mt-3 text-xs text-muted-foreground">
            <Link to="/login" className="hover:text-foreground transition-colors">
              Admin Login
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
